import { useQuery } from "@tanstack/react-query";
import { navigate, useQueryParams } from "raviger";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { toast } from "sonner";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import SearchInput from "@/components/Common/SearchInput";

import { getPermissions } from "@/common/Permissions";
import { PATIENT_GENDER_TYPES } from "@/common/constants";
import { canUseFullPatientActions } from "@/components/Patient/patientActionVisibility";
import useAuthUser from "@/hooks/useAuthUser";

import { PLUGIN_Component } from "@/PluginEngine";
import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import query from "@/Utils/request/query";
import { TableSkeleton } from "@/components/Common/SkeletonLoading";
import { usePermissions } from "@/context/PermissionContext";
import { useShortcuts, useShortcutSubContext } from "@/context/ShortcutContext";
import { cn } from "@/lib/utils";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import {
  getPartialId,
  PartialPatientModel,
  PatientRead,
} from "@/types/emr/patient/patient";
import patientApi from "@/types/emr/patient/patientApi";
import { PatientIdentifierConfig } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";
import careConfig from "@careConfig";
import { PlusIcon } from "@heroicons/react/24/outline";
import { TFunction } from "i18next";

const UHID_IDENTIFIER_SYSTEM = "https://aayushnet.com/id/uhid";

const isAadhaarSearchConfig = (config: PatientIdentifierConfig) => {
  const identifierText =
    `${config.config.display} ${config.config.system}`.toLowerCase();
  return ["aadhaar", "aadhar"].some((keyword) =>
    identifierText.includes(keyword),
  );
};

const isAbhaSearchConfig = (config: PatientIdentifierConfig) => {
  const identifierText =
    `${config.config.display} ${config.config.system}`.toLowerCase();
  return identifierText.includes("abha");
};

const isPhoneNumberSearchConfig = (config: PatientIdentifierConfig) => {
  return config.config.system === careConfig.phoneNumberConfigSystem;
};

const isPatientNameSearchConfig = (config: PatientIdentifierConfig) => {
  const normalizedDisplay = config.config.display
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const normalizedSystem = config.config.system
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  return (
    normalizedDisplay.includes("patientname") ||
    normalizedSystem.includes("patientname")
  );
};

const getSearchIdentifierPriority = (config: PatientIdentifierConfig) => {
  if (isPatientNameSearchConfig(config)) {
    return 0;
  }
  if (isPhoneNumberSearchConfig(config)) {
    return 1;
  }
  if (isAadhaarSearchConfig(config)) {
    return 2;
  }
  if (isAbhaSearchConfig(config)) {
    return 3;
  }
  if (config.config.system === UHID_IDENTIFIER_SYSTEM) {
    return 4;
  }
  return 5;
};

const getSearchableIdentifierConfigs = (facility?: {
  patient_instance_identifier_configs?: PatientIdentifierConfig[];
  patient_facility_identifier_configs?: PatientIdentifierConfig[];
}) => {
  const instanceConfigs = facility?.patient_instance_identifier_configs || [];
  const facilityConfigs = facility?.patient_facility_identifier_configs || [];
  const allConfigs = [...instanceConfigs, ...facilityConfigs];
  const patientNameConfig = allConfigs.find(isPatientNameSearchConfig);
  const phoneNumberConfig = allConfigs.find(isPhoneNumberSearchConfig);
  const aadhaarConfig = allConfigs.find(isAadhaarSearchConfig);
  const abhaConfig = allConfigs.find(isAbhaSearchConfig);
  const uhidConfig = allConfigs.find(
    (config) => config.config.system === UHID_IDENTIFIER_SYSTEM,
  );

  return [
    patientNameConfig,
    phoneNumberConfig,
    aadhaarConfig,
    abhaConfig,
    uhidConfig,
  ]
    .filter((config): config is PatientIdentifierConfig => !!config)
    .sort((a, b) => {
      const priorityDiff =
        getSearchIdentifierPriority(a) - getSearchIdentifierPriority(b);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      return a.config.display.localeCompare(b.config.display);
    });
};

export default function PatientIndex({ facilityId }: { facilityId: string }) {
  useShortcutSubContext("patient:search:-global");
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const shortcuts = useShortcuts();
  const [qParams] = useQueryParams();
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<
    PartialPatientModel | PatientRead | null
  >(null);
  const [verificationOpen, setVerificationOpen] = useState(false);

  const { facility } = useCurrentFacility();

  // Combine instance and facility identifier configs
  const allIdentifierConfigs = useMemo(
    () => getSearchableIdentifierConfigs(facility),
    [facility],
  );

  const facilityPermissions = getPermissions(
    hasPermission,
    facility?.permissions ?? [],
  );
  const { canCreatePatient } = facilityPermissions;

  const user = useAuthUser();
  const canCreatePatientVisible =
    canCreatePatient &&
    canUseFullPatientActions({
      isPractitionerWorkspace:
        facilityPermissions.canAccessPractitionerWorkspace,
      canAccessNursingActions: facilityPermissions.canAccessNursingActions,
      isSuperuser: user?.is_superuser,
    });

  useEffect(() => {
    shortcuts.setIgnoreInputFields(true);
    return () => shortcuts.setIgnoreInputFields(false);
  }, [shortcuts]);

  // Track identifier search state
  const [identifierSearch, setIdentifierSearch] = useState<{
    config?: string;
    value?: string;
  }>({});

  const handleSearch = useCallback((key: string, value: string) => {
    if (!value.trim()) {
      setIdentifierSearch({});
      return;
    }

    setIdentifierSearch({
      config: key,
      value,
    });
  }, []);

  const selectedIdentifierConfig = useMemo(
    () =>
      allIdentifierConfigs.find(
        (config) => config.id === identifierSearch.config,
      ),
    [allIdentifierConfigs, identifierSearch.config],
  );

  const isPatientNameSearch =
    !!selectedIdentifierConfig?.config.system &&
    isPatientNameSearchConfig(selectedIdentifierConfig);

  const {
    data: identifierSearchResults,
    isFetching: isFetchingIdentifierSearch,
  } = useQuery({
    queryKey: ["patient-search", facilityId, identifierSearch],
    queryFn: query.debounced(patientApi.search, {
      body: {
        config: identifierSearch.config,
        value: identifierSearch.value,
        facility: facilityId,
        page_size: 20,
      },
    }),
    enabled:
      !!(identifierSearch.config && identifierSearch.value) &&
      !isPatientNameSearch,
  });

  const { data: patientNameSearchResults, isFetching: isFetchingPatientName } =
    useQuery({
      queryKey: ["patient-search-by-name", facilityId, identifierSearch.value],
      queryFn: query.debounced(patientApi.list, {
        queryParams: {
          facility: facilityId,
          name: identifierSearch.value,
          limit: 20,
          offset: 0,
        },
      }),
      enabled:
        !!(identifierSearch.config && identifierSearch.value) &&
        isPatientNameSearch,
    });

  const patientList = isPatientNameSearch
    ? {
        partial: false,
        results: patientNameSearchResults?.results ?? [],
      }
    : identifierSearchResults;

  const isFetching = isPatientNameSearch
    ? isFetchingPatientName
    : isFetchingIdentifierSearch;

  const handlePatientSelect = (index: number) => {
    const patient = patientList?.results[index];
    if (!patient) {
      return;
    }
    if (patientList && patientList.partial) {
      setSelectedPatient(patient);
      setVerificationOpen(true);
      setYearOfBirth("");
    } else if ("year_of_birth" in patient) {
      navigate(`/facility/${facilityId}/patients/verify`, {
        query: {
          config: identifierSearch.config,
          value: identifierSearch.value,
          phone_number: patient.phone_number,
          year_of_birth: patient.year_of_birth?.toString() || "",
          partial_id: patient.id.slice(0, 5),
        },
      });
    }
  };

  useEffect(() => {
    shortcuts.setIgnoreInputFields(true);
    return () => shortcuts.setIgnoreInputFields(false);
  }, [shortcuts]);

  useEffect(() => {
    if (!facility) {
      return;
    }

    const phoneNumberConfig = getPhoneNumberConfig(allIdentifierConfigs);

    if (qParams.phone_number && phoneNumberConfig) {
      setIdentifierSearch({
        config: phoneNumberConfig.id,
        value: qParams.phone_number,
      });
    }
  }, [qParams.phone_number, facility, allIdentifierConfigs]);

  const handleVerify = () => {
    if (!selectedPatient || !yearOfBirth || yearOfBirth.length !== 4) {
      toast.error(t("valid_year_of_birth"));
      return;
    }

    navigate(`/facility/${facilityId}/patients/verify`, {
      query: {
        config: identifierSearch.config,
        value: identifierSearch.value,
        phone_number: selectedPatient.phone_number,
        year_of_birth: yearOfBirth,
        partial_id: getPartialId(selectedPatient),
      },
    });
  };

  return (
    <div>
      <div className="container max-w-5xl mx-auto py-6">
        {/* AayushNet Logo - Top right */}
        <div className="mb-6 flex items-center gap-0 justify-end">
          <img
            src="/images/aayushnet_logo_light.png"
            className="h-12"
            alt="AayushNet logo"
          />
          <h1
            className="text-2xl font-black tracking-wider"
            style={{
              fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
            }}
          >
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Aayush
            </span>
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Net
            </span>
          </h1>
        </div>

        {canCreatePatientVisible && (
          <div className="flex max-md:flex-col justify-center md:justify-end gap-4">
            <PLUGIN_Component
              __name="PatientSearchActions"
              facilityId={facilityId}
              className={cn(
                buttonVariants({ variant: "primary_gradient" }),
                "w-full",
              )}
            />
            <Button
              variant="outline"
              className="gap-3"
              onClick={() =>
                navigate(`/facility/${facilityId}/patient/register`)
              }
            >
              <PlusIcon className="size-4" />
              {t("patient_registration")}
            </Button>
            <AddPatientButton
              facilityId={facilityId}
              identifierConfigs={allIdentifierConfigs}
              identifierSearch={identifierSearch}
            />
          </div>
        )}
        <div className="space-y-6 mt-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {t("search_patients")}
            </h1>
            <p className="text-gray-500">
              {canCreatePatientVisible
                ? t("search_patient_page_text")
                : t("search_only_patient_page_text")}
            </p>
          </div>

          <div>
            <div className="space-y-6">
              <SearchInput
                options={getSearchOptions(
                  t,
                  identifierSearch,
                  allIdentifierConfigs,
                )}
                onSearch={handleSearch}
                className="w-full"
                autoFocus
              />

              <div className="min-h-[200px]" id="patient-search-results">
                {!!identifierSearch.config && !!identifierSearch.value && (
                  <>
                    {isFetching || !patientList ? (
                      <TableSkeleton count={5} />
                    ) : !patientList.results.length ? (
                      <div>
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                          <h3 className="text-lg font-semibold">
                            {t("no_patient_record_found")}
                          </h3>
                          <p className="text-sm text-gray-500 mb-6">
                            {t("no_patient_record_text", {
                              text: getSearchOptions(
                                t,
                                identifierSearch,
                                allIdentifierConfigs,
                              ).find(
                                (opt) => opt.key === identifierSearch.config,
                              )?.display,
                            })}
                          </p>
                          {canCreatePatientVisible && (
                            <AddPatientButton
                              facilityId={facilityId}
                              outline
                              identifierConfigs={allIdentifierConfigs}
                              identifierSearch={identifierSearch}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[300px]">
                                {t("patient_name")}
                              </TableHead>
                              <TableHead>{t("phone_number")}</TableHead>
                              <TableHead>{t("gender")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {patientList.results.map((patient, index) => (
                              <TableRow
                                key={patient.id}
                                className="cursor-pointer"
                                onClick={() => handlePatientSelect(index)}
                              >
                                <TableCell className="font-medium">
                                  {patient.name}
                                </TableCell>
                                <TableCell>
                                  {formatPhoneNumberIntl(patient.phone_number)}
                                </TableCell>
                                <TableCell>
                                  {
                                    PATIENT_GENDER_TYPES.find(
                                      (g) => g.id === patient.gender,
                                    )?.text
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={verificationOpen} onOpenChange={setVerificationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("verify_patient_identity")}</DialogTitle>
            <DialogDescription>
              {t("patient_birth_year_for_identity")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="text"
              placeholder={`${t("year_of_birth")} (YYYY)`}
              value={yearOfBirth}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,4}$/.test(value)) {
                  setYearOfBirth(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerify();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVerificationOpen(false)}
            >
              {t("cancel")}
            </Button>
            <Button className="mb-2" onClick={handleVerify}>
              {t("verify")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const getSearchOptions = (
  t: TFunction,
  searchIdentifier: { config?: string; value?: string },
  configs: PatientIdentifierConfig[],
) => {
  const getPriority = (config: PatientIdentifierConfig) => {
    if (isPatientNameSearchConfig(config)) {
      return 0;
    }
    if (config.config.system === careConfig.phoneNumberConfigSystem) {
      return 1;
    }
    if (config.config.system === UHID_IDENTIFIER_SYSTEM) {
      return 3;
    }
    return 2;
  };

  return [...configs]
    .sort((a, b) => {
      const priorityDiff = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      return a.config.display.localeCompare(b.config.display);
    })
    .map((c) => ({
      key: c.id,
      type:
        c.config.system === careConfig.phoneNumberConfigSystem
          ? ("phone" as const)
          : ("text" as const),
      placeholder: t("search_by_identifier", { name: c.config.display }),
      value:
        searchIdentifier.config === c.id ? (searchIdentifier.value ?? "") : "",
      display: c.config.display,
    }));
};

const getPhoneNumberConfig = (identifierConfigs: PatientIdentifierConfig[]) => {
  return identifierConfigs.find(
    (c) => c.config.system === careConfig.phoneNumberConfigSystem,
  );
};

const getPhoneNumberFromIdentifierSearch = (
  identifierConfigs: PatientIdentifierConfig[],
  identifierSearch: { config?: string; value?: string },
) => {
  const phoneNumberConfig = getPhoneNumberConfig(identifierConfigs);

  if (phoneNumberConfig && identifierSearch.config === phoneNumberConfig.id) {
    return identifierSearch.value;
  }

  return undefined;
};

function AddPatientButton({
  facilityId,
  outline,
  identifierConfigs,
  identifierSearch,
}: {
  facilityId: string;
  outline?: boolean;
  identifierConfigs: PatientIdentifierConfig[];
  identifierSearch?: { config?: string; value?: string };
}) {
  const { t } = useTranslation();

  const phoneNumber =
    identifierSearch &&
    getPhoneNumberFromIdentifierSearch(identifierConfigs, identifierSearch);

  return (
    <Button
      variant={outline ? "outline" : "primary_gradient"}
      className="gap-3 group"
      onClick={() =>
        navigate(`/facility/${facilityId}/patient/create`, {
          query: phoneNumber ? { phone_number: phoneNumber } : undefined,
        })
      }
      data-shortcut-id="submit-action"
    >
      <PlusIcon className="size-4" />
      {t("add_new_patient")}
      <ShortcutBadge actionId="submit-action" />
    </Button>
  );
}
